/**
 * The useCompanyDetails-Component provides CRUD-operations
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCompanyContactPersonNotes,
  getCompanyContactPersons,
  getCompanyDetails,
  updateCompany as updateCompanyApi,
} from "../../api/companies";
import { showErrorMessage, showSuccessMessage } from "../../utils/toastUtils";
import { UpdateCompanyDto } from "../../types/companyTypes";
import axios, { AxiosError } from "axios";
import { authReducerActionType } from "../../types/globalTypes";
import { useContext } from "react";
import { AuthContext } from "../../context/auth-context/AuthContext";
import { useNavigate } from "react-router-dom";

const useCompanyDetails = (companyId: number) => {
  const { dispatchAuth } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  /**
   * Returns true if the company id is valid, otherwise false
   */
  const isCompanyIdValid = () => {
    return companyId !== undefined && companyId !== null && companyId > 0;
  };

  // ############
  // GET QUERIES
  // ############

  // getCompanyDetails query
  const { data: companyDetailsData } = useQuery({
    queryKey: ["companyDetails", companyId],
    queryFn: () => getCompanyDetails(companyId),
    enabled: isCompanyIdValid(),
  });

  const companyDetails = companyDetailsData?.data;

  // getCompanyContactPersons query
  const { data: contactPersonsData } = useQuery({
    queryKey: ["contactPersons", companyId],
    queryFn: () => getCompanyContactPersons(companyId),
    enabled: isCompanyIdValid(),
  });

  const contactPersons = contactPersonsData?.data;

  // getCompanyContactPersonNotes query
  const { data: companyContactPersonNotesData } = useQuery({
    queryKey: ["companyContactPersonNotes", companyId],
    queryFn: () => getCompanyContactPersonNotes(companyId),
    enabled: isCompanyIdValid(),
  });

  const companyContactPersonNotes = companyContactPersonNotesData?.data || [];

  // ----------------------------------------------------------------------------------

  // ############
  // UPDATE QUERIES
  // ############

  // update company mutation
  const { mutateAsync: updateCompanyMutation } = useMutation({
    mutationFn: (updatedCompany: UpdateCompanyDto) => updateCompanyApi(companyId, updatedCompany),
    onError: (err: AxiosError) => {
      if (err.response?.status === 401) {
        dispatchAuth({ type: authReducerActionType.deauthenticate });
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["companies"] });
      await queryClient.invalidateQueries({ queryKey: ["companyDetails", companyId] });
      showSuccessMessage("Kunde erfolgreich aktualisiert");
      navigate(`/unternehmen/${companyId}`);
    },
  });

  /**
   * Updates an existing company
   * @param updatedCompany The company data to update the company with
   */
  const updateCompany = async (updatedCompany: UpdateCompanyDto) => {
    try {
      await updateCompanyMutation(updatedCompany);
    } catch (error: unknown) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const message = error.response.data?.message;
        const normalizedUpdatedUrl = updatedCompany.url?.trim() || null;

        // TODO: Not ideal, use custom problem codes from the backend to differentiate the error
        if (message === `Company with name '${updatedCompany.name}' already exists`) {
          showErrorMessage("Es existiert bereits ein Kunde mit diesem Namen");
          return;
        }

        // TODO: Not ideal, use custom problem codes from the backend to differentiate the error
        if (normalizedUpdatedUrl && message === `Company with URL '${normalizedUpdatedUrl}' already exists`) {
          showErrorMessage("Es existiert bereits ein Kunde mit dieser Website");
          return;
        }
      } else {
        showErrorMessage("Kunde aktualisieren fehlgeschlagen");
        return;
      }
    }
  };

  return { companyDetails, contactPersons, companyContactPersonNotes, updateCompany };
};

export default useCompanyDetails;
